import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { FaInfoCircle, FaExclamationTriangle, FaExclamationCircle } from "react-icons/fa";
import { useIsAuthenticated } from "@azure/msal-react";
import { useAccessToken } from '../hooks/useAuth';
import cocAssistantService from '../services/cocAssistantService';

const AlertContext = createContext();

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

export const AlertProvider = ({ children }) => {
    const [alerts, setAlerts] = useState({
        info: null,
        alert: null,
        error: null
    });

    // Track if a modal is currently open
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Track processing jobs
    const [processingJobs, setProcessingJobs] = useState([]);
    const [completedJobs, setCompletedJobs] = useState([]);
    
    const isAuthenticated = useIsAuthenticated();
    const { getBackendAccessToken } = useAccessToken();

    // Memoize all setter functions with useCallback to prevent unnecessary re-renders
    const setInfoAlert = useCallback((message) => {
        setAlerts(prev => ({ ...prev, info: { icon: <FaInfoCircle />, message } }));
    }, []);

    const setAlert = useCallback((message) => {
        setAlerts(prev => ({ ...prev, alert: { icon: <FaExclamationTriangle />, message } }));
    }, []);

    const setErrorAlert = useCallback((message) => {
        setAlerts(prev => ({ ...prev, error: { icon: <FaExclamationCircle />, message } }));
    }, []);

    const clearInfoAlert = useCallback(() => {
        setAlerts(prev => ({ ...prev, info: null }));
    }, []);

    const clearAlert = useCallback(() => {
        setAlerts(prev => ({ ...prev, alert: null }));
    }, []);

    const clearErrorAlert = useCallback(() => {
        setAlerts(prev => ({ ...prev, error: null }));
    }, []);

    const clearAllAlerts = useCallback(() => {
        setAlerts({
            info: null,
            alert: null,
            error: null
        });
    }, []);

    // Add a processing job to track
    const addProcessingJob = useCallback((jobId, fileName, jobType = 'COC_PARSING') => {
        setProcessingJobs(prev => {
            // Don't add if already exists
            if (prev.some(job => job.jobId === jobId)) {
                return prev;
            }
            return [...prev, { 
                jobId, 
                fileName, 
                jobType,
                status: 'PENDING',
                addedAt: new Date().toISOString()
            }];
        });
    }, []);

    // Remove a job from processing list
    const removeProcessingJob = useCallback((jobId) => {
        setProcessingJobs(prev => prev.filter(job => job.jobId !== jobId));
    }, []);

    // Mark a job as completed (for notification purposes)
    const markJobCompleted = useCallback((jobId, result) => {
        setProcessingJobs(prev => prev.filter(job => job.jobId !== jobId));
        setCompletedJobs(prev => {
            // Don't add if already exists
            if (prev.some(job => job.jobId === jobId)) {
                return prev;
            }
            return [...prev, {
                jobId,
                result,
                completedAt: new Date().toISOString(),
                fileName: result.fileName || 'Unknown'
            }];
        });
    }, []);

    // Clear a specific completed job
    const clearCompletedJob = useCallback((jobId) => {
        setCompletedJobs(prev => prev.filter(job => job.jobId !== jobId));
    }, []);

    // Clear all completed jobs
    const clearCompletedJobs = useCallback(() => {
        setCompletedJobs([]);
    }, []);

    // Poll processing jobs every 60 seconds
    useEffect(() => {
        if (!isAuthenticated || processingJobs.length === 0) {
            return;
        }

        const pollJobs = async () => {
            try {
                const accessToken = await getBackendAccessToken();
                
                // Check each job status
                for (const job of processingJobs) {
                    try {
                        const statusResponse = await cocAssistantService.getJobStatus(accessToken, job.jobId);
                        
                        if (statusResponse.status === 'COMPLETED') {
                            // Job completed - fetch result and notify user
                            try {
                                const resultResponse = await cocAssistantService.getJobResult(accessToken, job.jobId);
                                console.log(`Job ${job.jobId} completed:`, resultResponse);
                                markJobCompleted(job.jobId, resultResponse);
                                setInfoAlert(`COC processing completed: ${job.fileName}`);
                            } catch (resultErr) {
                                console.error(`Error fetching result for job ${job.jobId}:`, resultErr);
                                removeProcessingJob(job.jobId);
                                setErrorAlert(`COC processing completed but result unavailable: ${job.fileName}`);
                            }
                        } else if (statusResponse.status === 'FAILED') {
                            // Job failed - notify user
                            console.error(`Job ${job.jobId} failed:`, statusResponse);
                            removeProcessingJob(job.jobId);
                            setErrorAlert(`COC processing failed: ${job.fileName}`);
                        }
                        // Otherwise, keep polling (PENDING or PROCESSING)
                    } catch (err) {
                        console.error(`Error checking job ${job.jobId}:`, err);
                        // Don't remove job on error, will retry next poll
                    }
                }
            } catch (err) {
                console.error('Error polling jobs:', err);
            }
        };

        // Initial poll
        pollJobs();

        // Set up interval (every 60 seconds)
        const interval = setInterval(pollJobs, 60000);

        return () => clearInterval(interval);
    }, [isAuthenticated, processingJobs, getBackendAccessToken, markJobCompleted, removeProcessingJob, setInfoAlert, setErrorAlert]);

    const value = useMemo(() => ({
        alerts,
        isModalOpen,
        setIsModalOpen,
        setInfoAlert,
        setAlert,
        setErrorAlert,
        clearInfoAlert,
        clearAlert,
        clearErrorAlert,
        clearAllAlerts,
        processingJobs,
        completedJobs,
        addProcessingJob,
        removeProcessingJob,
        markJobCompleted,
        clearCompletedJob,
        clearCompletedJobs
    }), [
        alerts, 
        isModalOpen, 
        setInfoAlert, 
        setAlert, 
        setErrorAlert, 
        clearInfoAlert, 
        clearAlert, 
        clearErrorAlert, 
        clearAllAlerts,
        processingJobs,
        completedJobs,
        addProcessingJob,
        removeProcessingJob,
        markJobCompleted,
        clearCompletedJob,
        clearCompletedJobs
    ]);

    return (
        <AlertContext.Provider value={value}>
            {children}
        </AlertContext.Provider>
    );
};
